import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import { collectionService } from '../../services/collectionService';
import { memberService } from '../../services/memberService';
import toast from 'react-hot-toast';

const DailyCollection = () => {
  const [selectedSamity, setSelectedSamity] = useState('');
  const [collectionDate, setCollectionDate] = useState(new Date().toISOString().split('T')[0]);
  const [collectionItems, setCollectionItems] = useState([]);
  const [officerId, setOfficerId] = useState('');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  const { data: samitiesData } = useQuery(
    ['collection-samities'],
    () => collectionService.getSamities()
  );

  const { data: dueListResponse, refetch } = useQuery(
    ['due-list', selectedSamity, collectionDate],
    () => collectionService.getDueList(selectedSamity, collectionDate),
    {
      enabled: !!selectedSamity,
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to load due list');
      }
    }
  );

  const samities = samitiesData?.data || [];

  // Safe extraction with fallback values - FIXED
  const dueList = dueListResponse?.data || [];
  const totalDueAmount = dueListResponse?.total_due_amount || 0;
  const totalMembers = dueListResponse?.total_members || 0;

  const submitMutation = useMutation(
    (data) => collectionService.createDailyCollection(data),
    {
      onSuccess: () => {
        toast.success('Collection sheet submitted successfully!');
        setCollectionItems([]);
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Submission failed');
      }
    }
  );

  const handleAmountChange = (memberId, type, amount) => {
    setCollectionItems(prev => {
      const existing = prev.find(item => item.member_id === memberId);
      const member = dueList.find(m => m.member_id === memberId);

      const totalDue = member?.due_items?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;

      if (existing) {
        if (type === 'loan') {
          existing.loan_amount = parseFloat(amount) || 0;
        } else {
          existing.savings_amount = parseFloat(amount) || 0;
        }
        existing.total_amount = existing.loan_amount + existing.savings_amount;
        return [...prev];
      } else {
        const newItem = {
          member_id: memberId,
          member_code: member?.member_code || '',
          member_name: member?.member_name || '',
          phone: member?.phone || '',
          savings_amount: type === 'savings' ? (parseFloat(amount) || 0) : 0,
          loan_amount: type === 'loan' ? (parseFloat(amount) || 0) : 0,
          total_amount: parseFloat(amount) || 0,
          total_due: totalDue,
          loan_installment_id: member?.due_items?.find(item => item.type === 'loan')?.installment_id
        };
        return [...prev, newItem];
      }
    });
  };

  const handleSubmit = () => {
    if (!selectedSamity) {
      toast.error('Please select a Samity');
      return;
    }

    if (collectionItems.length === 0) {
      toast.error('No collection items added');
      return;
    }

    submitMutation.mutate({
      collection_date: collectionDate,
      samity_id: parseInt(selectedSamity),
      field_officer_id: parseInt(officerId) || 1,
      items: collectionItems
    });
  };

  const calculateTotal = () => {
    return collectionItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);
  };

  // ========== WHATSAPP INTEGRATION ==========

  const generateWhatsAppMessage = (member) => {
    const dueItems = member.due_items || [];
    const totalDue = dueItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);
    const memberName = member.member_name || 'Valued Member';
    const date = new Date(collectionDate).toLocaleDateString('bn-BD', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    let message = `*Samity Management System*\n\n`;
    message += `*দৈনিক সংগ্রহ বিজ্ঞপ্তি*\n`;
    message += `───────────────────\n`;
    message += `প্রিয় ${memberName},\n\n`;
    message += `আপনার বকেয়া টাকা জমা দেওয়ার জন্য অনুরোধ করা হচ্ছে।\n\n`;
    message += `*তারিখ:* ${date}\n`;
    message += `*বকেয়া পরিমাণ:* ৳${totalDue.toLocaleString()}\n\n`;

    if (dueItems.length > 0) {
      message += `*বিস্তারিত:*\n`;
      dueItems.forEach((item, index) => {
        if (item.type === 'loan') {
          message += `  ${index + 1}. ঋণের কিস্তি #${item.installment_no || ''}: ৳${(item.total_amount || 0).toLocaleString()}\n`;
        } else if (item.type === 'savings') {
          const months = item.missed_months?.map(m => `${m.month_name}`).join(', ') || '';
          message += `  ${index + 1}. সঞ্চয় (${item.missed_count || 0} মাস): ৳${(item.total_amount || 0).toLocaleString()}\n`;
          if (months) {
            message += `     (মিস: ${months})\n`;
          }
        }
      });
      message += `\n`;
    }

    message += `দয়া করে দ্রুত আপনার বকেয়া পরিশোধ করুন।\n\n`;
    message += `ধন্যবাদ!\n`;
    message += `━━━━━━━━━━━━━━━━━━━\n`;
    message += `*Samity Management System*`;

    return message;
  };

 const generateWhatsAppLink = (phone, message) => {
  let formattedPhone = phone?.replace(/\D/g, '') || '';

  // Bangladesh format: 017xxxxxxxx -> 88017xxxxxxxx
  if (formattedPhone.startsWith('0')) {
    formattedPhone = `88${formattedPhone}`;
  } else if (formattedPhone.startsWith('1') && formattedPhone.length === 10) {
    formattedPhone = `880${formattedPhone}`;
  } else if (formattedPhone.startsWith('88')) {
    // already ok
  }

  return `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
};

  const handleSendWhatsApp = (member) => {
    const message = generateWhatsAppMessage(member);
    const link = generateWhatsAppLink(member.phone, message);

    window.open(link, '_blank');
  };

  const handleSendBulkWhatsApp = () => {
    if (!dueList || dueList.length === 0) {
      toast.error('No members to send reminders to');
      return;
    }

    setSendingWhatsApp(true);

    const members = dueList;
    let currentIndex = 0;

    const sendNext = () => {
      if (currentIndex >= members.length) {
        setSendingWhatsApp(false);
        toast.success('All WhatsApp messages opened!');
        return;
      }

      const member = members[currentIndex];
      const message = generateWhatsAppMessage(member);
      const link = generateWhatsAppLink(member.phone, message);

      window.open(link, '_blank');
      currentIndex++;

      setTimeout(sendNext, 3000);
    };

    toast.info(`Opening WhatsApp for ${members.length} members...`);
    sendNext();
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Daily Collection Sheet</h1>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-2">Samity/Group</label>
            <select
              value={selectedSamity}
              onChange={(e) => setSelectedSamity(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">Select Samity</option>
              {samities.map((samity) => (
                <option key={samity.id} value={samity.id}>
                  {samity.samity_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Collection Date</label>
            <input
              type="date"
              value={collectionDate}
              onChange={(e) => setCollectionDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Field Officer</label>
            <input
              type="text"
              value={officerId}
              onChange={(e) => setOfficerId(e.target.value)}
              placeholder="Officer ID"
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div className="flex space-x-2 flex-wrap gap-2">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Load Due List
          </button>

          {dueList && dueList.length > 0 && (
            <>
              <button
                onClick={handleSendBulkWhatsApp}
                disabled={sendingWhatsApp}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.01-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                </svg>
                {sendingWhatsApp ? 'Sending...' : 'Send All WhatsApp'}
              </button>
              <span className="text-sm text-gray-500 self-center">
                Total Due: ৳{(totalDueAmount || 0).toLocaleString()} ({totalMembers || 0} members)
              </span>
            </>
          )}
        </div>
      </div>

      {dueList && dueList.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Due</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Savings</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Collecting</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">WhatsApp</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dueList.map((member) => {
                  const totalDue = member.due_items?.reduce((sum, item) => sum + (item.total_amount || 0), 0) || 0;
                  const collection = collectionItems.find(item => item.member_id === member.member_id);

                  const savingsItems = member.due_items?.filter(item => item.type === 'savings') || [];
                  const loanItems = member.due_items?.filter(item => item.type === 'loan') || [];

                  const savingsDue = savingsItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);
                  const loanDue = loanItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);

                  return (
                    <tr key={member.member_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium">{member.member_name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{member.member_code || 'N/A'}</div>
                        <div className="text-xs text-gray-400">{member.phone || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4">
                        {savingsItems.map((item, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="text-gray-600">সঞ্চয় ({item.missed_count || 0} মাস)</span>
                            <div className="text-xs text-gray-400">
                              {item.missed_months?.map(m => m.month_name).join(', ') || ''}
                            </div>
                          </div>
                        ))}
                        {loanItems.map((item, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="text-gray-600">ঋণের কিস্তি #{item.installment_no || ''}</span>
                            <div className="text-xs text-gray-400">
                              Due: {item.due_date ? new Date(item.due_date).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-red-600 font-bold">৳ {(totalDue || 0).toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-green-600">৳ {(savingsDue || 0).toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-blue-600">৳ {(loanDue || 0).toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="number"
                          value={collection?.total_amount || ''}
                          onChange={(e) => {
                            const amount = parseFloat(e.target.value) || 0;
                            setCollectionItems(prev => {
                              const existing = prev.find(item => item.member_id === member.member_id);
                              if (existing) {
                                existing.total_amount = amount;
                                return [...prev];
                              } else {
                                return [...prev, {
                                  member_id: member.member_id,
                                  member_code: member.member_code || '',
                                  member_name: member.member_name || '',
                                  savings_amount: 0,
                                  loan_amount: 0,
                                  total_amount: amount,
                                  total_due: totalDue
                                }];
                              }
                            });
                          }}
                          placeholder="Enter amount"
                          className="w-32 px-2 py-1 border rounded"
                        />
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleSendWhatsApp(member)}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title="Send WhatsApp reminder"
                        >
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.01-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="2" className="px-6 py-4 text-right font-bold">Total Collection:</td>
                  <td className="px-6 py-4 font-bold text-red-600">৳ {(calculateTotal() || 0).toLocaleString()}</td>
                  <td colSpan="4"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="px-6 py-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Total Due: ৳{(totalDueAmount || 0).toLocaleString()} from {totalMembers || 0} members
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitMutation.isLoading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {submitMutation.isLoading ? 'Submitting...' : 'Submit Collection Sheet'}
            </button>
          </div>
        </div>
      )}

      {dueList && dueList.length === 0 && selectedSamity && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-800">No due items found for this samity.</p>
          <p className="text-sm text-yellow-600 mt-1">All members are up to date with their payments.</p>
        </div>
      )}
    </div>
  );
};

export default DailyCollection;